import { Controller } from '@hotwired/stimulus'
import { instrumentaLaMicrointervencion } from '../lib/analitica_microintervenciones'

// Connects to data-controller="microintervencion"
//
// Ciclo de vida de la microintervención inline (TC-134-1.6.2.1). El bloque ya lo pinta el servidor
// y se lee sin este controlador; lo que añade aquí son las fases del handoff de Diseño (§5):
//
//   viva ──aceptar──▶ confirmación (~1,5 s) ──▶ pliegue ──▶ cerrada (separador)
//   viva ──rechazar / cerrar──────────────────▶ pliegue ──▶ cerrada
//
// y los eventos que el host escucha (§4): `microintervencion:accept`, `microintervencion:reject` y
// `microintervencion:dismiss`, con `{ type, variant, t }` en `detail` —y `us` si el host la pasa—.
// Ignorar no emite nada.
//
// El controlador NO sabe qué hace cada botón. Si el botón va dentro de un formulario (el servidor lo
// pinta así cuando el host le pasa una URL), envía ese formulario por fetch; si no, solo emite el
// evento y la acción es asunto del host.
//
// Esos eventos son además lo que instrumenta el embudo: `lib/analitica_microintervenciones.js`
// (TC-134-1.4.8.6) los escucha y emite `microintervention_shown` / `_acted` / `_dismissed`.
//
// ACEPTAR espera a que la acción responda bien antes de confirmar: el mensaje de éxito afirma algo
// ("ahora aparecerá en tu feed") y no puede enseñarse si no ha pasado. Si falla, la intervención
// vuelve a estar viva y se emite `microintervencion:error`. DESCARTAR, en cambio, no espera: el
// handoff pide pliegue inmediato, y registrar el rechazo no le promete nada a quien lo pide.
export default class extends Controller {
  static targets = ['pliegue', 'tarjeta', 'icono', 'iconoConfirmacion', 'cuerpo', 'confirmacion', 'boton', 'separador']

  static values = {
    // Clave que viaja en los eventos; el controlador no la interpreta.
    variante: String,
    // User Story de la variante (handoff §4). Opcional: solo viaja en los eventos si el host la pasa.
    us: String,
    // Mensaje de la fase de confirmación. Llega como valor y no pintado en el DOM porque la región
    // viva tiene que estar VACÍA hasta ese momento: solo anuncia cambios.
    confirmacion: String,
    confirmacionMs: { type: Number, default: 1500 },
    // Algo por encima de la transición CSS (260 ms): al cerrar, la transición ya ha terminado.
    pliegueMs: { type: Number, default: 280 }
  }

  connect () {
    this.fase = 'viva'
    this.temporizadores = []
    // Los botones sin formulario nacen ocultos (sin JS no harían nada). Ahora sí hacen algo.
    this.botonTargets.forEach(boton => { boton.hidden = false })
    // Analítica del embudo (TC-134-1.4.8.6): emite `microintervention_shown` y deja escuchando los
    // tres eventos de abajo para emitir `_acted` y `_dismissed`. Se engancha AQUÍ, en el chasis, para
    // que cualquier variante que lo renderice nazca instrumentada sin acordarse de nada. El
    // controlador no sabe nada más de analítica: qué evento sale de cada interacción lo decide
    // `lib/analitica_microintervenciones.js`.
    instrumentaLaMicrointervencion(this.varianteValue)
  }

  disconnect () {
    this.temporizadores.forEach(clearTimeout)
    this.temporizadores = []
  }

  async acepta (event) {
    event.preventDefault()
    if (this.fase !== 'viva') return

    const boton = event.currentTarget
    if (boton.form) {
      this.fase = 'enviando'
      this.ocupada(true)
      const bien = await this.envia(boton.form, boton)
      if (this.fase !== 'enviando') return
      this.ocupada(false)

      if (!bien) {
        this.fase = 'viva'
        this.emite('error')
        return
      }
    }

    this.emite('accept')
    this.confirma()
  }

  rechaza (event) {
    event.preventDefault()
    if (this.fase !== 'viva') return

    const boton = event.currentTarget
    const tipo = event.params?.motivo === 'dismiss' ? 'dismiss' : 'reject'

    this.emite(tipo)
    if (boton.form) this.envia(boton.form, boton, { keepalive: true })
    this.retieneElFoco()
    this.pliega()
  }

  // ─── Fases ───

  confirma () {
    this.fase = 'confirmando'
    this.cuerpoTarget.hidden = true
    this.botonTargets.forEach(boton => { boton.hidden = true })
    this.iconoTarget.hidden = true
    this.iconoConfirmacionTarget.hidden = false
    this.tarjetaTarget.classList.add('rnk-Microintervencion_Tarjeta-confirmacion')
    this.confirmacionTarget.textContent = this.confirmacionValue
    this.retieneElFoco()
    this.programa(() => this.pliega(), this.confirmacionMsValue)
  }

  pliega () {
    this.fase = 'plegando'
    this.element.classList.add('rnk-Microintervencion-plegada')
    this.programa(() => this.cierra(), this.pliegueMsValue)
  }

  cierra () {
    this.fase = 'cerrada'
    this.pliegueTarget.hidden = true
    this.element.classList.add('rnk-Microintervencion-cerrada')
    if (this.hasSeparadorTarget) this.separadorTarget.hidden = false
  }

  // ─── Apoyo ───

  // `{ type, variant, t }` es el contrato de eventos del handoff (§4).
  emite (tipo) {
    const detail = { type: tipo, variant: this.varianteValue, t: Date.now() }
    if (this.hasUsValue) detail.us = this.usValue
    this.dispatch(tipo, { detail })
  }

  // Quien pulsó con el teclado se quedaría con el foco en un botón que acaba de desaparecer. Se
  // lleva a la tarjeta (tabindex -1), que sigue en su sitio: el siguiente Tab continúa desde ahí.
  retieneElFoco () {
    if (this.element.contains(document.activeElement)) this.tarjetaTarget.focus({ preventScroll: true })
  }

  ocupada (si) {
    this.botonTargets.forEach(boton => { boton.disabled = si })
    this.tarjetaTarget.toggleAttribute('aria-busy', si)
  }

  programa (funcion, ms) {
    this.temporizadores.push(setTimeout(funcion, ms))
  }

  // Envía el formulario tal cual lo pintó el servidor (token CSRF y `_method` incluidos), más el
  // `name`/`value` del botón pulsado, que es lo que distingue "Ahora no" de la X.
  async envia (formulario, boton, { keepalive = false } = {}) {
    const datos = new FormData(formulario)
    if (boton.name) datos.append(boton.name, boton.value)

    try {
      const respuesta = await fetch(formulario.getAttribute('action'), {
        method: 'POST',
        body: datos,
        credentials: 'same-origin',
        keepalive,
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content ?? ''
        }
      })
      return respuesta.ok
    } catch {
      return false
    }
  }
}
